import { create } from 'zustand';

/**
 * UI state only. Nothing fetched from the server lives here — TanStack Query
 * owns that, and duplicating it into Zustand is how two copies of the truth
 * start disagreeing.
 *
 * What belongs here: what is open, what is being announced, and which world the
 * shell is currently dressed as.
 */

let toastId = 0;

export const useUi = create((set, get) => ({
  /* ------------------------------------------------------------- overlays */
  cartOpen: false,
  menuOpen: false,
  searchOpen: false,
  filtersOpen: false,

  openCart: () => set({ cartOpen: true, menuOpen: false, searchOpen: false }),
  closeCart: () => set({ cartOpen: false }),

  toggleMenu: () => set((state) => ({ menuOpen: !state.menuOpen, cartOpen: false })),
  closeMenu: () => set({ menuOpen: false }),

  openSearch: () => set({ searchOpen: true, menuOpen: false, cartOpen: false }),
  closeSearch: () => set({ searchOpen: false }),

  toggleFilters: () => set((state) => ({ filtersOpen: !state.filtersOpen })),
  closeFilters: () => set({ filtersOpen: false }),

  closeAll: () => set({ cartOpen: false, menuOpen: false, searchOpen: false, filtersOpen: false }),

  /* ---------------------------------------------------------------- world */

  /**
   * Which accent the shell is wearing. Set by the route, read by the layout
   * wrapper as a data attribute — the colour itself is resolved in CSS, so there
   * is no JavaScript colour logic anywhere.
   */
  world: null,
  setWorld: (world) => {
    if (get().world !== world) set({ world });
  },

  /* --------------------------------------------------------------- toasts */

  toasts: [],

  toast: ({ title, description, tone = 'default', duration = 4500, action, image, meta }) => {
    const id = (toastId += 1);
    set((state) => ({
      toasts: [...state.toasts, { id, title, description, tone, action, image, meta }],
    }));

    if (duration > 0) {
      setTimeout(() => get().dismissToast(id), duration);
    }

    return id;
  },

  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter((entry) => entry.id !== id) })),

  /* ------------------------------------------------------- flying to cart */

  /**
   * The add-to-cart animation needs the source rectangle, and only the component
   * that was clicked knows it. It is parked here for the one frame the animation
   * reads it.
   */
  flyFrom: null,
  setFlyFrom: (rect) => set({ flyFrom: rect }),
  clearFlyFrom: () => set({ flyFrom: null }),

  /* -------------------------------------------------------- recently seen */

  recentlyViewed: readRecent(),
  rememberProduct: (slug) => {
    const next = [slug, ...get().recentlyViewed.filter((entry) => entry !== slug)].slice(0, 8);
    set({ recentlyViewed: next });
    try {
      localStorage.setItem('ge:recent', JSON.stringify(next));
    } catch {
      // Private browsing and full quotas both throw. Losing the list is fine.
    }
  },
}));

function readRecent() {
  try {
    const raw = localStorage.getItem('ge:recent');
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, 8) : [];
  } catch {
    return [];
  }
}

/** Convenience for the common case, callable outside a component. */
export const toast = (options) => useUi.getState().toast(options);
